import React from 'react';

const PrivacyPolicy = ({ onClose }) => {
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="flex justify-between items-center mb-8 sticky top-0 bg-white py-4 border-b">
          <h1 className="text-3xl font-bold text-gray-900">Privacy Policy</h1>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
            data-testid="privacy-close-btn"
          >
            &times;
          </button>
        </div>

        {/* Content */}
        <div className="prose prose-gray max-w-none">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Privacy Policy for Decimal Skincare</h2>
            <p className="text-gray-500">Effective Date: 18-06-2025</p>
            <p className="text-gray-500">Last Updated: 18-06-2025</p>
          </div>

          <p className="text-gray-600 mb-8">
            Decimal Skincare ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, share, and protect your personal information when you use our mobile application and related services (collectively, the "App").
          </p>

          <p className="text-gray-600 mb-8">
            By using Decimal Skincare, you agree to the collection and use of your information in accordance with this policy.
          </p>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Information We Collect</h2>
            
            <h3 className="text-lg font-medium text-gray-800 mb-3">a. Personal Information</h3>
            <p className="text-gray-600 mb-3">When you create an account or use our app, we may collect:</p>
            <ul className="list-disc list-inside text-gray-600 mb-4 space-y-1">
              <li>Name</li>
              <li>Email address</li>
              <li>Mobile number</li>
              <li>Gender and age</li>
              <li>Skin type and concerns</li>
              <li>Photos or images of your face (for skin analysis)</li>
              <li>Location (if permitted)</li>
              <li>Health and lifestyle data (optional): e.g., menstrual cycle, sleep patterns, physical activity</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mb-3">b. Device & Usage Information</h3>
            <p className="text-gray-600 mb-3">We automatically collect:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-1">
              <li>Device type and OS</li>
              <li>App usage data</li>
              <li>IP address</li>
              <li>Crash reports and diagnostics</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">2. How We Use Your Information</h2>
            <p className="text-gray-600 mb-3">We use your data to:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-1">
              <li>Analyze your skin condition using AI</li>
              <li>Recommend personalized skincare routines and products</li>
              <li>Track your skincare progress</li>
              <li>Improve app performance and features</li>
              <li>Send reminders, updates, and promotional content (with your consent)</li>
              <li>Ensure security and prevent fraud</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">3. How We Share Your Information</h2>
            <p className="text-gray-600 mb-3">We do not sell your personal information. We may share information with:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-1">
              <li><strong>Service providers:</strong> For cloud storage, analytics, and customer support</li>
              <li><strong>Dermatology partners or skincare brands</strong> (only with your explicit consent)</li>
              <li><strong>Legal authorities:</strong> If required by law, or to protect rights and safety</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Your Rights and Choices</h2>
            <p className="text-gray-600 mb-3">You have the right to:</p>
            <ul className="list-disc list-inside text-gray-600 mb-4 space-y-1">
              <li>Access and update your personal data</li>
              <li>Withdraw consent at any time</li>
              <li>Request deletion of your account and data</li>
              <li>Opt out of promotional communications</li>
            </ul>
            <p className="text-gray-600">
              You can manage these settings directly in the app or by contacting us.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Data Security</h2>
            <p className="text-gray-600">
              We use industry-standard encryption and security practices to protect your data. However, no system is 100% secure. We encourage you to use strong passwords and update your app regularly.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Data Retention</h2>
            <p className="text-gray-600">
              We retain your data as long as your account is active or as needed to provide services. You may request deletion at any time.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Children's Privacy</h2>
            <p className="text-gray-600">
              Decimal Skincare is not intended for use by individuals under the age of 13. We do not knowingly collect data from children.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">8. International Users</h2>
            <p className="text-gray-600">
              If you are accessing our app from outside India, your information may be transferred to, stored, and processed in India or other jurisdictions where our servers are located.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Changes to This Policy</h2>
            <p className="text-gray-600">
              We may update this Privacy Policy from time to time. We will notify you of material changes via email or app notifications.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Contact Us</h2>
            <p className="text-gray-600 mb-4">For questions or concerns, contact us at:</p>
            <div className="text-gray-600">
              <p className="font-semibold">Decimal Skincare</p>
              <p>Email: care@decimalskincare.com</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
